import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import {
  useGroupAttendanceSummaryQuery,
  GroupAttendanceSummaryQuery as GroupAttendanceSummaryDoc,
  useGroupPerformanceSummaryQuery,
  GroupPerformanceSummaryQuery as GroupPerformanceSummaryDoc,
  useGroupRevenueAggregatesQuery,
  GroupRevenueAggregatesQuery as GroupRevenueAggregatesDoc,
  useGroupLessonsQuery,
  GroupLessonsQuery as GroupLessonsDoc,
  useAllZoomRecordingsQuery,
  AllZoomRecordingsQuery as AllZoomRecordingsDoc,
  useGroupMaterialsQuery,
  GroupMaterialsQuery as GroupMaterialsDoc,
  usePandaDocDocumentsQuery,
  PandaDocDocumentsQuery as PandaDocDocumentsDoc,
  MaterialKindEnum,
} from 'core/graphql';

import { useStableRefetch } from './useStableRefetch';

/**
 * `refetch` выключенного (`skip`) запроса. Apollo 3.3 держит под skip живой
 * `ObservableQuery`, и его `refetch()` УХОДИТ В СЕТЬ — ровно тот запрос,
 * который роль не читает. Поэтому у выключенного запроса refetch — пустышка.
 */
const SKIPPED_REFETCH = (): void => undefined;

/* ---------- group-scoped attendance summary (TZ §G3) ---------- */

export type IGroupAttendanceSummary = NonNullable<
  GroupAttendanceSummaryDoc['groupAttendanceSummary']
>;

interface IGroupAttendanceSummaryHook {
  data?: IGroupAttendanceSummary;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * `skip` — роль `groupAttendanceSummary` не читает
 * (`useGroupPermissions().canReadAttendanceSummary === false`): запрос не уходит.
 */
export const useGroupAttendanceSummary = (
  groupId?: string,
  skip?: boolean,
): IGroupAttendanceSummaryHook => {
  const skipped = !groupId || Boolean(skip);
  const { data, loading, error, refetch } = useGroupAttendanceSummaryQuery({
    variables: { groupId: groupId || '' },
    skip: skipped,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    data: data?.groupAttendanceSummary ?? undefined,
    loading,
    error,
    refetch: useStableRefetch(skipped ? SKIPPED_REFETCH : refetch),
  };
};

/* ---------- group-scoped performance summary (TZ §G4) ---------- */

export type IGroupPerformanceSummary = NonNullable<
  GroupPerformanceSummaryDoc['groupPerformanceSummary']
>;

interface IGroupPerformanceSummaryHook {
  data?: IGroupPerformanceSummary;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/** `skip` — роль `groupPerformanceSummary` не читает (`canReadPerformanceSummary`). */
export const useGroupPerformanceSummary = (
  groupId?: string,
  skip?: boolean,
): IGroupPerformanceSummaryHook => {
  const skipped = !groupId || Boolean(skip);
  const { data, loading, error, refetch } = useGroupPerformanceSummaryQuery({
    variables: { groupId: groupId || '' },
    skip: skipped,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    data: data?.groupPerformanceSummary ?? undefined,
    loading,
    error,
    refetch: useStableRefetch(skipped ? SKIPPED_REFETCH : refetch),
  };
};

/* ---------- group-scoped revenue rollup (TZ §G5) ---------- */

export type IGroupRevenueAggregates = NonNullable<
  GroupRevenueAggregatesDoc['groupRevenueAggregates']
>;

interface IGroupRevenueAggregatesHook {
  data?: IGroupRevenueAggregates;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/** `skip` — роль `groupRevenueAggregates` не читает (`canReadRevenue`). */
export const useGroupRevenueAggregates = (
  groupId?: string,
  skip?: boolean,
): IGroupRevenueAggregatesHook => {
  const skipped = !groupId || Boolean(skip);
  const { data, loading, error, refetch } = useGroupRevenueAggregatesQuery({
    variables: { groupId: groupId || '' },
    skip: skipped,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    data: data?.groupRevenueAggregates ?? undefined,
    loading,
    error,
    refetch: useStableRefetch(skipped ? SKIPPED_REFETCH : refetch),
  };
};

/* ---------- group schedule + lessons (lazy, for ScheduleTab) ---------- */

export type IGroupForLessons = NonNullable<GroupLessonsDoc['group']>;
export type IGroupLessonRow = NonNullable<
  NonNullable<IGroupForLessons['lessons']>[number]
>;

interface IGroupLessonsHook {
  group?: IGroupForLessons;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useGroupLessons = (id?: string): IGroupLessonsHook => {
  const { data, loading, error, refetch } = useGroupLessonsQuery({
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    group: data?.group ?? undefined,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

/* ---------- group-scoped Zoom recordings (TZ §G2) ---------- */

export type IGroupZoomRecording = NonNullable<
  NonNullable<
    NonNullable<AllZoomRecordingsDoc['allZoomRecordings']>['edges'][number]
  >['node']
>;

interface IGroupZoomRecordingsHook {
  recordings: IGroupZoomRecording[];
  hasNextPage: boolean;
  endCursor?: string | null;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

interface IGroupZoomRecordingsOpts {
  groupId?: string;
  dateFrom?: string;
  dateTo?: string;
  orphanOnly?: boolean;
  first?: number;
  /** Не запрашивать вовсе — роль без `ZOOM_ACCESS_ROLES` (`canSeeZoomTab`). */
  skip?: boolean;
}

export const useGroupZoomRecordings = (
  opts: IGroupZoomRecordingsOpts,
): IGroupZoomRecordingsHook => {
  const skipped = !opts.groupId || Boolean(opts.skip);
  const { data, loading, error, refetch } = useAllZoomRecordingsQuery({
    variables: {
      groupId: opts.groupId,
      dateFrom: opts.dateFrom,
      dateTo: opts.dateTo,
      orphanOnly: opts.orphanOnly,
      first: opts.first ?? 50,
    },
    skip: skipped,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const edges = data?.allZoomRecordings?.edges || [];
  return {
    recordings: edges
      .map((e) => e?.node)
      .filter((n): n is IGroupZoomRecording => Boolean(n)),
    hasNextPage: Boolean(data?.allZoomRecordings?.pageInfo?.hasNextPage),
    endCursor: data?.allZoomRecordings?.pageInfo?.endCursor,
    loading,
    error,
    refetch: useStableRefetch(skipped ? SKIPPED_REFETCH : refetch),
  };
};

/* ---------- group materials (TZ §G6) ---------- */

export type IGroupMaterial = NonNullable<
  GroupMaterialsDoc['materials'][number]
>;

interface IGroupMaterialsHook {
  materials: IGroupMaterial[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

interface IGroupMaterialsOpts {
  groupId?: string;
  lessonId?: string;
  kind?: MaterialKindEnum;
  /** Роль без `MATERIALS_READ_ROLES` (`canSeeMaterialsTab`) — не запрашивать. */
  skip?: boolean;
}

export const useGroupMaterials = (
  opts: IGroupMaterialsOpts,
): IGroupMaterialsHook => {
  const skipped = !opts.groupId || Boolean(opts.skip);
  const { data, loading, error, refetch } = useGroupMaterialsQuery({
    variables: {
      groupId: opts.groupId || '',
      lessonId: opts.lessonId,
      kind: opts.kind,
    },
    skip: skipped,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return {
    materials: (data?.materials || []).filter((m): m is IGroupMaterial =>
      Boolean(m),
    ),
    loading,
    error,
    refetch: useStableRefetch(skipped ? SKIPPED_REFETCH : refetch),
  };
};

/* ---------- group PandaDoc documents (TZ §G1) ---------- */

/** One PandaDoc document node of `pandadocDocuments(groupId)`. */
export type IGroupPandaDocDocument = NonNullable<
  NonNullable<
    NonNullable<PandaDocDocumentsDoc['pandadocDocuments']>['edges'][number]
  >['node']
>;

interface IGroupPandaDocDocumentsHook {
  documents: IGroupPandaDocDocument[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

interface IGroupPandaDocDocumentsOpts {
  groupId?: string;
  first?: number;
  /** Роль без `PANDADOC_ACCESS_ROLES` (`canSeePandadocTab`) — не запрашивать. */
  skip?: boolean;
}

/**
 * PandaDoc-документы договоров группы — вкладка «PandaDoc» и сигнал
 * «Проблемы с PandaDoc». Один запрос с одними переменными (`first: 100`) у
 * обоих потребителей: Apollo отдаёт второй из кеша.
 */
export const useGroupPandaDocDocuments = (
  opts: IGroupPandaDocDocumentsOpts,
): IGroupPandaDocDocumentsHook => {
  const skipped = !opts.groupId || Boolean(opts.skip);
  const { data, loading, error, refetch } = usePandaDocDocumentsQuery({
    variables: { groupId: opts.groupId, first: opts.first ?? 100 },
    skip: skipped,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const documents = useMemo(
    () =>
      (data?.pandadocDocuments?.edges || [])
        .map((edge) => edge?.node)
        .filter((node): node is IGroupPandaDocDocument => Boolean(node)),
    [data],
  );
  return {
    documents,
    loading,
    error,
    refetch: useStableRefetch(skipped ? SKIPPED_REFETCH : refetch),
  };
};
